import {
	Col,
	DatePicker,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	notification,
	Button,
} from "antd";
import React, { useEffect, useState } from "react";
import { api } from "../utils/Api";
import axios from "axios";
import { CustomSpinner } from "../utils/CustomComponents";
import { slumpOptions } from "../../constants/Job";
import dayjs from "dayjs";

const PreJobInView = ({ preJobId, isEdit }) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(true);
	const [loadingButton, setLoadingButton] = useState(false);
	const [initialValues, setInitialValues] = useState({});
	const [apiNotification, contextHolder] = notification.useNotification();

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	useEffect(() => {
		getPREJob();
	}, []);

	const getPREJob = async () => {
		try {
			setLoading(true);

			const url = api + `/prejob/get/${preJobId}`;
			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.get(url);
			if (data.success) {
				const payload = data.payload;
				setInitialValues({
					user: payload.user,
					customer: payload.customer,
					targetYD3: payload.targetYD3,
					price: payload.price,
					mix: payload.mix,
					foreman: payload.foreman,
					slump: payload.slump,
					plant: payload.plant,
					customerShipAddress: payload.customerShipAddress,
					scheduledDate: dayjs(payload.scheduledDate),
					registerDate: dayjs(payload.registerDate),
					jobNumber: payload.job ? payload.job.number : "N/A",
				});
				setLoading(false);
			} else {
				setLoading(false);
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			setLoading(false);
			openNotification("error", "Error", "Error while trying to get PRE Job");
			console.error(err.message);
			return null;
		}
	};

	const updatePREJob = async (values) => {
		try {
			setLoadingButton(true);

			const url = api + `/prejob/update/${preJobId}`;
			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const body = {
				...values,
				scheduledDate: dayjs(values.scheduledDate).toDate(),
			};

			delete body.registerDate;
			delete body.jobNumber;

			const { data } = await axios.patch(url, body);
			if (data.success) {
				setLoadingButton(false);
				openNotification("success", "Success", data.message);
			} else {
				setLoadingButton(false);
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			setLoadingButton(false);
			openNotification(
				"error",
				"Error",
				"Error while trying to update PRE Job"
			);
			console.error(err.message);
			return null;
		}
	};

	const onFinish = (values) => {
		updatePREJob(values);
	};

	const CustomForm = () => {
		return (
			<Form
				form={form}
				layout="vertical"
				onFinish={onFinish}
				style={{ marginTop: "20px" }}
				initialValues={initialValues}
			>
				<Row gutter={16}>
					<Col span={8}>
						<Form.Item
							label="User"
							name="user"
							rules={[
								{ type: "email", message: "Email is invalid" },
								{ required: true, message: "User is required" },
							]}
						>
							<Input size="large" readOnly={!isEdit} />
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item
							label="Customer"
							name="customer"
							rules={[{ required: true, message: "Customer is required" }]}
						>
							<Input size="large" readOnly={!isEdit} />
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item
							label="Target YD3"
							name="targetYD3"
							rules={[{ required: true, message: "Target YD3 is required" }]}
						>
							<InputNumber
								size="large"
								style={{ width: "100%" }}
								readOnly={!isEdit}
								formatter={(value) =>
									`${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
								}
								parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item
							label="Price"
							name="price"
							rules={[{ required: true, message: "Price is required" }]}
						>
							<InputNumber
								size="large"
								style={{ width: "100%" }}
								readOnly={!isEdit}
								formatter={(value) =>
									`$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
								}
								parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item
							label="Mix"
							name="mix"
							rules={[{ required: true, message: "Mix is required" }]}
						>
							<Input size="large" readOnly={!isEdit} />
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item
							label="Foreman"
							name="foreman"
							rules={[{ required: true, message: "Foreman is required" }]}
						>
							<Input size="large" readOnly={!isEdit} />
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item
							label="Slump"
							name="slump"
							rules={[{ required: true, message: "Slump is required" }]}
						>
							<Select
								style={{
									width: "100%",
								}}
								size="large"
								placeholder="Please select slump"
								options={slumpOptions}
								optionFilterProp="children"
								disabled={!isEdit}
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item
							label="Plant"
							name="plant"
							rules={[{ required: true, message: "Plant is required" }]}
						>
							<Input size="large" readOnly={!isEdit} />
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item
							label="Customer Shipping Address"
							name="customerShipAddress"
							rules={[
								{
									required: true,
									message: "Customer Shipping Address is required",
								},
							]}
						>
							<Input size="large" readOnly={!isEdit} />
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item
							label="Scheduled Date"
							name="scheduledDate"
							rules={[
								{
									required: true,
									message: "Scheduled Date is required",
								},
							]}
						>
							<DatePicker
								size="large"
								readOnly={!isEdit}
								showTime={{ format: "HH:mm" }}
								format="MM/DD/YYYY HH:mm A"
								disabled={!isEdit}
								style={{ width: "100%" }}
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item
							label="Register Date"
							name="registerDate"
							rules={[
								{
									required: true,
									message: "Register Date is required",
								},
							]}
						>
							<DatePicker
								size="large"
								readOnly={true}
								showTime={{ format: "HH:mm" }}
								format="MM/DD/YYYY HH:mm A"
								disabled={true}
								style={{ width: "100%" }}
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item label="Job Number" name="jobNumber">
							<Input size="large" readOnly={!isEdit} />
						</Form.Item>
					</Col>
				</Row>
				{isEdit && (
					<Row>
						<Col>
							<Button
								size="large"
								htmlType="submit"
								type="primary"
								disabled={loadingButton}
								loading={loadingButton}
							>
								Save
							</Button>
						</Col>
					</Row>
				)}
			</Form>
		);
	};

	return (
		<>
			{contextHolder}
			{loading ? <CustomSpinner /> : <CustomForm />}
		</>
	);
};

export default PreJobInView;
